<script>
  import Dialog from "@/components/dialog/Dialog.svelte"
  import SwitchInput from "@/components/form/SwitchInput.svelte"
  import LoadingSpinner from "@/components/widgets/LoadingSpinner.svelte"
  import { getContext, onDestroy } from "svelte"
  import { numberToCurrency } from "utils"
  import { writable } from "svelte/store"

  export let storageUnits = []

  let dialogIsOpenStore = null
  let storageUnitsMap = {}
  let siteMapDialogStoreUnsubscribe = () => {}
  let storageUnit = { customer: { id: "", name: "" }, id: "", locked: false, name: "" }

  const loadingStore = writable({ disabled: false, locked: false })
  const siteMapDialogStore = getContext("dialogStore")
  const siteMapReload = getContext("siteMapReload")

  function arrayToMapById(array) {
    const out = {}

    array.forEach((item) => {
      out[item.id] = item
    })

    return out
  }

  function buildHeaderProps($storageUnit) {
    function joinClasses(p) {
      p.closeButtonClasses = p.closeButtonClasses.join(" ")
      p.styleClasses = p.styleClasses.join(" ")
      return p
    }

    const baseClasses = ["transition-colors"]
    const baseCloseButtonClasses = []

    const props = {
      closeButtonClasses: baseCloseButtonClasses,
      statusLabel: "",
      styleClasses: baseClasses,
    }

    if (!$storageUnit) {
      props.closeButtonClasses = baseCloseButtonClasses
      props.styleClasses = [...baseClasses, "text-slate-600"]

      return joinClasses(props)
    }

    if ($storageUnit.occupancyStatus === "occupied") {
      props.closeButtonClasses = [...baseCloseButtonClasses, "hover:bg-emerald-600", "text-white"]
      props.statusLabel = "Occupied"
      props.styleClasses = [...baseClasses, "bg-emerald-500", "border-emerald-600", "text-white"]
    }

    if ($storageUnit.occupancyStatus === "past_due") {
      props.closeButtonClasses = [...baseCloseButtonClasses, "hover:bg-red-700", "text-white"]
      props.statusLabel = "Past Due"
      props.styleClasses = [...baseClasses, "bg-red-600", "border-red-800", "text-white"]
    }

    if ($storageUnit.occupancyStatus === "vacant") {
      props.closeButtonClasses = [...baseCloseButtonClasses, "hover:bg-slate-100", "text-slate-600"]
      props.statusLabel = "Vacant"
      props.styleClasses = [...baseClasses, "text-slate-600"]
    }

    if ($storageUnit.disabled) {
      props.closeButtonClasses = [...baseCloseButtonClasses, "hover:bg-gray-900", "text-white"]
      props.statusLabel = "Disabled"
      props.styleClasses = [...baseClasses, "bg-gray-900/80", "border-black", "text-white"]
    }

    if ($storageUnit.scheduledMoveIn) {
      const moveInString = new Date($storageUnit.scheduledMoveIn).toLocaleDateString()

      props.closeButtonClasses = [...baseCloseButtonClasses, "hover:bg-blue-600", "text-white"]
      props.statusLabel = `Scheduled Move In (${moveInString})`
      props.styleClasses = [...baseClasses, "bg-blue-500", "border-blue-600", "text-white"]
    }

    if ($storageUnit.scheduledMoveOut) {
      const moveOutString = new Date($storageUnit.scheduledMoveOut).toLocaleDateString()

      props.closeButtonClasses = [...baseCloseButtonClasses, "hover:bg-purple-700", "text-white"]
      props.statusLabel = `Scheduled Move Out (${moveOutString})`
      props.styleClasses = [...baseClasses, "bg-purple-600", "border-purple-800", "text-white"]
    }

    if ($storageUnit.locked) {
      props.closeButtonClasses = [...baseCloseButtonClasses, "hover:bg-cyan-600", "text-white"]
      props.statusLabel = "Locked"
      props.styleClasses = [...baseClasses, "bg-cyan-500", "border-cyan-700", "text-white"]
    }

    if ($storageUnit.auction) {
      props.closeButtonClasses = [
        ...baseCloseButtonClasses,
        "border-slate-700",
        "hover:bg-yellow-400",
        "text-slate-700",
      ]
      props.statusLabel = "In Auction"
      props.styleClasses = [...baseClasses, "bg-yellow-300", "border-yellow-500", "text-slate-700"]
    }

    return joinClasses(props)
  }

  function handleAttributeChange({ clientAttr, serverAttr }) {
    return async (to) => {
      if (!storageUnit) return

      const from = storageUnit[clientAttr]

      if (from == to) return

      if ($loadingStore[clientAttr]) return

      loadingStore.update((current) => ({ ...current, [clientAttr]: true }))

      storageUnit[clientAttr] = to

      const response = await fetch(`/company_admin/storage_units/${storageUnit.id}`, {
        body: JSON.stringify({ storage_unit: { [serverAttr]: to } }),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").getAttribute("content"),
        },
        method: "PATCH",
      })

      if (!response.ok) {
        storageUnit[clientAttr] = from
        loadingStore.update((current) => ({ ...current, [clientAttr]: false }))

        throw new Error(`Failed to save storage unit row ${clientAttr} state`)
      }

      await siteMapReload({ shouldCenterMap: false, shouldFetchStatuses: true })

      loadingStore.update((current) => ({ ...current, [clientAttr]: false }))
    }
  }

  function handleDisabledToggle() {
    handleAttributeChange({ clientAttr: "disabled", serverAttr: "disabled" })(!isDisabled)
  }

  function handleLockedToggle() {
    handleAttributeChange({ clientAttr: "locked", serverAttr: "locked_out" })(!isLocked)
  }

  siteMapDialogStoreUnsubscribe = siteMapDialogStore.subscribe((current) => {
    storageUnit = storageUnitsMap[current.storageUnitId]

    dialogIsOpenStore?.set(current.isOpen)
  })

  onDestroy(() => {
    siteMapDialogStoreUnsubscribe()
  })

  $: headerProps = buildHeaderProps(storageUnit)
  $: isDisabled = !!storageUnit?.disabled
  $: isLocked = !!storageUnit?.locked
  $: storageUnitsMap = arrayToMapById(storageUnits)
</script>

<Dialog
  bind:isOpenStore={dialogIsOpenStore}
  closeButtonClasses={headerProps.closeButtonClasses}
  headerClasses={headerProps.styleClasses}
>
  <h3 class="flex flex-col font-bold gap-2 grow min-w-0 self-center" slot="header">
    <div class="flex gap-4 items-center">
      <svg
        aria-hidden="true"
        class="h-8 shrink-0 w-8"
        fill="currentColor"
        id="cube"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12.378 1.602a.75.75 0 00-.756 0L3 6.632l9 5.25 9-5.25-8.622-5.03zM21.75 7.93l-9 5.25v9l8.628-5.032a.75.75 0 00.372-.648V7.93zM11.25 22.18v-9l-9-5.25v8.57a.75.75 0 00.372.648l8.628 5.033z"
        />
      </svg>
      <a
        class="flex flex-col focus:outline-none hover:underline truncate underline-offset-4"
        data-turbo-frame="_top"
        href="/company_admin/storage_units/{storageUnit?.id}"
        title="Unit #{storageUnit?.name}"
      >
        <span>Unit: {storageUnit?.name}</span>

        <span class="font-normal text-xs">
          {storageUnit?.dimensions}
        </span>
      </a>
    </div>

    {#if storageUnit?.customer}
      <p class="font-bold text-sm">
        Balance: {numberToCurrency(storageUnit.customer.balance)}
      </p>
    {/if}

    {#if headerProps.statusLabel}
      <p class="font-bold text-sm">
        Status: {headerProps.statusLabel}
      </p>
    {/if}
  </h3>
  <div class="flex flex-col gap-4 items-center justify-center p-4" slot="body">
    {#if storageUnit?.customer}
      <a
        class="border border-emerald-600 bg-emerald-500 font-bold gap-2 hover:bg-emerald-600 hover:border-emerald-700 hover:shadow-lg inline-flex items-center justify-between p-4 rounded-md shadow-md text-white transition-colors w-full"
        data-turbo-frame="_top"
        href="/company_admin/customers/{storageUnit.customer.id}/payments"
      >
        <div class="flex gap-2 items-center">
          <svg
            aria-hidden="true"
            class="h-6 w-6"
            fill="currentColor"
            id="currency-dollar"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 01-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004zM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 01-.921.42z"
            />
            <path
              clip-rule="evenodd"
              d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v.816a3.836 3.836 0 00-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 01-.921-.421l-.879-.66a.75.75 0 00-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 001.5 0v-.81a4.124 4.124 0 001.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 00-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 00.933-1.175l-.415-.33a3.836 3.836 0 00-1.719-.755V6z"
              fill-rule="evenodd"
            />
          </svg>
          <span>Make a Payment</span>
        </div>

        <svg
          aria-hidden="true"
          class="h-5 w-5"
          fill="currentColor"
          id="arrow-top-right-on-square"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clip-rule="evenodd"
            d="M15.75 2.25H21a.75.75 0 01.75.75v5.25a.75.75 0 01-1.5 0V4.81L8.03 17.03a.75.75 0 01-1.06-1.06L19.19 3.75h-3.44a.75.75 0 010-1.5zm-10.5 4.5a1.5 1.5 0 00-1.5 1.5v10.5a1.5 1.5 0 001.5 1.5h10.5a1.5 1.5 0 001.5-1.5V10.5a.75.75 0 011.5 0v8.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V8.25a3 3 0 013-3h8.25a.75.75 0 010 1.5H5.25z"
            fill-rule="evenodd"
          />
        </svg>
      </a>

      <a
        class="border border-slate-300 bg-slate-100 font-bold gap-2 hover:bg-slate-200 hover:shadow-lg inline-flex items-center justify-between p-4 rounded-md shadow-md text-slate-500 transition-colors w-full"
        data-turbo-frame="_top"
        href="/company_admin/customers/{storageUnit.customer.id}"
      >
        <div class="flex gap-2 items-center">
          <svg
            aria-hidden="true"
            class="h-6 w-6"
            fill="currentColor"
            id="user"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clip-rule="evenodd"
              d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z"
              fill-rule="evenodd"
            />
          </svg>
          <span>{storageUnit.customer.name}</span>
        </div>

        <svg
          aria-hidden="true"
          class="h-5 w-5"
          fill="currentColor"
          id="arrow-top-right-on-square"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clip-rule="evenodd"
            d="M15.75 2.25H21a.75.75 0 01.75.75v5.25a.75.75 0 01-1.5 0V4.81L8.03 17.03a.75.75 0 01-1.06-1.06L19.19 3.75h-3.44a.75.75 0 010-1.5zm-10.5 4.5a1.5 1.5 0 00-1.5 1.5v10.5a1.5 1.5 0 001.5 1.5h10.5a1.5 1.5 0 001.5-1.5V10.5a.75.75 0 011.5 0v8.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V8.25a3 3 0 013-3h8.25a.75.75 0 010 1.5H5.25z"
            fill-rule="evenodd"
          />
        </svg>
      </a>
    {/if}

    <a
      class="border border-slate-300 bg-slate-100 font-bold gap-2 hover:bg-slate-200 hover:shadow-lg inline-flex items-center justify-between p-4 rounded-md shadow-md text-slate-500 transition-colors w-full"
      data-turbo-frame="_top"
      href="/company_admin/storage_units/{storageUnit?.id}/edit"
    >
      <div class="flex gap-2 items-center">
        <svg
          aria-hidden="true"
          class="h-6 w-6"
          fill="currentColor"
          id="pencil-square"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32l8.4-8.4z"
          />
          <path
            d="M5.25 5.25a3 3 0 00-3 3v10.5a3 3 0 003 3h10.5a3 3 0 003-3V13.5a.75.75 0 00-1.5 0v5.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5V8.25a1.5 1.5 0 011.5-1.5h5.25a.75.75 0 000-1.5H5.25z"
          />
        </svg>
        <span>Edit Unit</span>
      </div>

      <svg
        aria-hidden="true"
        class="h-5 w-5"
        fill="currentColor"
        id="arrow-top-right-on-square"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          clip-rule="evenodd"
          d="M15.75 2.25H21a.75.75 0 01.75.75v5.25a.75.75 0 01-1.5 0V4.81L8.03 17.03a.75.75 0 01-1.06-1.06L19.19 3.75h-3.44a.75.75 0 010-1.5zm-10.5 4.5a1.5 1.5 0 00-1.5 1.5v10.5a1.5 1.5 0 001.5 1.5h10.5a1.5 1.5 0 001.5-1.5V10.5a.75.75 0 011.5 0v8.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V8.25a3 3 0 013-3h8.25a.75.75 0 010 1.5H5.25z"
          fill-rule="evenodd"
        />
      </svg>
    </a>

    {#if storageUnit?.occupancyStatus === "vacant"}
      <button
        class="border disabled:cursor-not-allowed disabled:opacity-60 font-bold gap-2 hover:shadow-lg inline-flex justify-between items-center p-4 rounded-md shadow-md transition-colors w-full"
        class:bg-slate-100={!isDisabled}
        class:border-slate-300={!isDisabled}
        class:hover:bg-slate-200={!isDisabled}
        class:bg-gray-900={isDisabled}
        class:bg-opacity-80={isDisabled}
        class:border-black={isDisabled}
        class:hover:bg-opacity-90={isDisabled}
        class:hover:border-black={isDisabled}
        class:text-slate-500={!isDisabled}
        class:text-white={isDisabled}
        data-turbo-frame="_top"
        disabled={$loadingStore.disabled}
        on:click={handleDisabledToggle}
        type="button"
      >
        <div class="flex gap-2 items-center">
          {#if isDisabled}
            <svg
              aria-hidden="true"
              class="h-6 w-6"
              fill="currentColor"
              id="check-circle"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                clip-rule="evenodd"
                d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                fill-rule="evenodd"
              />
            </svg>
          {:else}
            <svg
              aria-hidden="true"
              class="h-6 w-6"
              fill="currentColor"
              id="no-symbol"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                clip-rule="evenodd"
                d="M6.72 5.66l11.62 11.62A8.25 8.25 0 006.72 5.66zm10.56 12.68L5.66 6.72a8.25 8.25 0 0011.62 11.62zM5.105 5.106c3.807-3.808 9.98-3.808 13.788 0 3.808 3.807 3.808 9.98 0 13.788-3.807 3.808-9.98 3.808-13.788 0-3.808-3.807-3.808-9.98 0-13.788z"
                fill-rule="evenodd"
              />
            </svg>
          {/if}
          <span>{isDisabled ? "Enable" : "Disable"} Unit</span>
        </div>

        {#if $loadingStore.disabled}
          <LoadingSpinner classNames="h-7 w-7" />
        {:else}
          <SwitchInput
            onChange={handleAttributeChange({ clientAttr: "disabled", serverAttr: "disabled" })}
            value={!!storageUnit?.disabled}
          />
        {/if}
      </button>

      <a
        class="border border-blue-700 bg-blue-500 font-bold gap-2 hover:bg-blue-600 hover:shadow-lg inline-flex items-center justify-between p-4 rounded-md shadow-md text-white transition-colors w-full"
        data-turbo-frame="_top"
        href="/company_admin/move_in_wizard/choose_customer?storage_unit_id={storageUnit.id}"
      >
        <div class="flex gap-2 items-center">
          <svg
            aria-hidden="true"
            class="h-6 w-6"
            fill="currentColor"
            id="archive-box"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.375 3C2.339 3 1.5 3.84 1.5 4.875v.75c0 1.036.84 1.875 1.875 1.875h17.25c1.035 0 1.875-.84 1.875-1.875v-.75C22.5 3.839 21.66 3 20.625 3H3.375z"
            />
            <path
              clip-rule="evenodd"
              d="M3.087 9l.54 9.176A3 3 0 006.62 21h10.757a3 3 0 002.995-2.824L20.913 9H3.087zm6.163 3.75A.75.75 0 0110 12h4a.75.75 0 010 1.5h-4a.75.75 0 01-.75-.75z"
              fill-rule="evenodd"
            />
          </svg>
          <span>Move In</span>
        </div>

        <svg
          aria-hidden="true"
          class="h-5 w-5"
          fill="currentColor"
          id="arrow-top-right-on-square"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clip-rule="evenodd"
            d="M15.75 2.25H21a.75.75 0 01.75.75v5.25a.75.75 0 01-1.5 0V4.81L8.03 17.03a.75.75 0 01-1.06-1.06L19.19 3.75h-3.44a.75.75 0 010-1.5zm-10.5 4.5a1.5 1.5 0 00-1.5 1.5v10.5a1.5 1.5 0 001.5 1.5h10.5a1.5 1.5 0 001.5-1.5V10.5a.75.75 0 011.5 0v8.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V8.25a3 3 0 013-3h8.25a.75.75 0 010 1.5H5.25z"
            fill-rule="evenodd"
          />
        </svg>
      </a>
    {:else}
      <button
        class="border disabled:cursor-not-allowed disabled:opacity-60 font-bold gap-2 hover:shadow-lg inline-flex justify-between items-center p-4 rounded-md shadow-md transition-colors w-full"
        class:bg-slate-100={!isLocked}
        class:border-slate-300={!isLocked}
        class:hover:bg-slate-200={!isLocked}
        class:bg-cyan-300={isLocked}
        class:border-cyan-500={isLocked}
        class:hover:bg-cyan-400={isLocked}
        class:hover:border-cyan-600={isLocked}
        class:text-slate-500={!isLocked}
        class:text-cyan-700={isLocked}
        data-turbo-frame="_top"
        disabled={$loadingStore.locked}
        on:click={handleLockedToggle}
        type="button"
      >
        <div class="flex gap-2 items-center">
          {#if isLocked}
            <svg
              aria-hidden="true"
              class="h-6 w-6"
              fill="currentColor"
              id="lock"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                clip-rule="evenodd"
                d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z"
                fill-rule="evenodd"
              />
            </svg>
          {:else}
            <svg
              aria-hidden="true"
              class="h-6 w-6"
              fill="currentColor"
              id="lock-open"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 1.5c2.9 0 5.25 2.35 5.25 5.25v3.75a.75.75 0 01-1.5 0V6.75a3.75 3.75 0 10-7.5 0v3a3 3 0 013 3v6.75a3 3 0 01-3 3H3.75a3 3 0 01-3-3v-6.75a3 3 0 013-3h9v-3c0-2.9 2.35-5.25 5.25-5.25z"
              />
            </svg>
          {/if}
          <span>{isLocked ? "Unlock" : "Lock"} Unit</span>
        </div>

        {#if $loadingStore.locked}
          <LoadingSpinner classNames="h-7 w-7" />
        {:else}
          <SwitchInput
            onChange={handleAttributeChange({ clientAttr: "locked", serverAttr: "locked_out" })}
            value={!!storageUnit?.locked}
          />
        {/if}
      </button>
    {/if}
  </div>
</Dialog>
